import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Sparkles, Loader2, ShieldCheck, Settings } from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { isTrialActive, isTrialExpired } from "../lib/trialStatus";
import api from "../lib/api";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshMe } = useAuth();
  const { t, lang } = useT();
  // Translated feature list — keys live in /app/frontend/src/lib/i18n.js so
  // both EN and ES versions render correctly when the app language toggles.
  const FEATURES = [
    t("pricing.feature1"), t("pricing.feature2"), t("pricing.feature3"),
    t("pricing.feature4"), t("pricing.feature5"), t("pricing.feature6"),
    t("pricing.feature7"), t("pricing.feature8"),
    // New value-bullets — placed before "Cancel anytime" so the cancel
    // bullet remains the natural closer of the list. Order follows the
    // customer journey: capture → respond → quote → review → win-back →
    // referral, then the cancel-friendly reassurance line.
    t("pricing.feature10"), t("pricing.feature11"),
    t("pricing.feature12"), t("pricing.feature13"),
    t("pricing.feature14"),
    t("pricing.feature9"),
  ];
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  // When Stripe portal call fails because the saved customer/subscription is
  // a stale test-mode record (common after swapping to live keys), flip this
  // and show a live-checkout upgrade path instead of a dead "Manage billing".
  const [portalBroken, setPortalBroken] = useState(false);

  // Always pull the latest subscription state from the backend whenever we land
  // on /pricing, including when the browser restores the page from BFCache after
  // returning from Stripe Checkout (back button or Stripe's "Back" link). This
  // prevents a stale localStorage user from showing "Manage billing" when the
  // user is not actually subscribed.
  useEffect(() => {
    refreshMe();
    const onShow = (e) => {
      if (e.persisted || document.visibilityState === "visible") {
        refreshMe();
      }
    };
    window.addEventListener("pageshow", onShow);
    document.addEventListener("visibilitychange", onShow);
    return () => {
      window.removeEventListener("pageshow", onShow);
      document.removeEventListener("visibilitychange", onShow);
    };
  }, [refreshMe]);

  useEffect(() => {
    if (location.hash === "#pricing") {
      const el = document.getElementById("pricing");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  const subscribe = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", {
        plan_id: "pro_monthly",
        origin_url: origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/portal", { origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      const detail = String(err?.response?.data?.detail || err?.message || "");
      // Detect stale test-mode customer / subscription / "No such" Stripe errors
      // and route the user into a fresh live checkout instead of looping on portal.
      const isStaleTestRecord = /no such (customer|subscription)|similar object exists in test mode|no such/i.test(
        detail
      );
      if (isStaleTestRecord) {
        setPortalBroken(true);
        toast.error("Your old billing record is no longer valid. Please subscribe again.");
      } else {
        toast.error(detail || "Could not open billing portal");
      }
      setPortalLoading(false);
    }
  };

  const isActive = user?.subscription_status === "active";
  const manageBillingUsable = isActive && !portalBroken;
  const trialActive = useMemo(() => isTrialActive(user), [user]);
  const trialExpired = useMemo(() => isTrialExpired(user), [user]);
  const ctaLabel = manageBillingUsable
    ? t("pricing.ctaManage")
    : portalBroken
      ? t("pricing.ctaUpgradeNow")
      : trialActive
        ? t("pricing.ctaUpgradeNow")
        : trialExpired
          ? t("pricing.ctaUpgradeUnlock")
          : t("pricing.ctaStartTrial");

  return (
    <div className="min-h-screen flex flex-col" data-testid="pricing-page">
      <Navbar />
      <main className="flex-1">
        <section id="pricing" className="scroll-mt-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("pricing.eyebrow")}</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl font-semibold text-gray-900 tracking-tight">
              {t("pricing.title")}
            </h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              {t("pricing.subtitle")}
            </p>
          </div>

          <div className="mt-14 max-w-xl mx-auto">
            <div className="rounded-3xl bg-white border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-br from-green-800 to-green-900 text-white p-8">
                <div className="flex flex-wrap items-center gap-2 text-green-100 text-sm">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> {t("pricing.tierName")}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full bg-white/15 border border-white/20 text-[11px] font-semibold tracking-wide uppercase text-green-50"
                    data-testid="pricing-early-access-badge"
                  >
                    {t("pricing.earlyAccess")}
                  </span>
                  {isActive && !portalBroken && (
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-xs font-semibold" data-testid="active-subscription-badge">
                      {t("pricing.active")}
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-6xl font-bold tracking-tight text-white" data-testid="pricing-price">$39</span>
                  <span className="text-green-100">{t("pricing.perMonth")}</span>
                </div>
                <p className="mt-2 text-sm text-green-200/90 text-center" data-testid="pricing-trial-line">
                  {t("pricing.trialLine")}
                </p>
              </div>

              <div className="p-8">
                <ul className="space-y-3.5">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-green-100 text-green-800 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3" />
                      </span>
                      <span className="text-gray-700 text-sm leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                {manageBillingUsable ? (
                  <div className="mt-8 space-y-3">
                    <Button
                      onClick={openPortal}
                      disabled={portalLoading}
                      className="w-full h-12 bg-green-800 hover:bg-green-900 text-white text-base"
                      data-testid="pricing-manage-subscription-button"
                    >
                      {portalLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Settings className="w-4 h-4 mr-2" /> {ctaLabel}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      {t("pricing.portalNote")}
                    </p>
                  </div>
                ) : (
                  <>
                    {portalBroken && (
                      <p
                        className="mt-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center"
                        data-testid="pricing-stale-billing-note"
                      >
                        {t("pricing.staleBilling")}
                      </p>
                    )}
                    <Button
                      onClick={subscribe}
                      disabled={loading}
                      className="mt-8 w-full h-13 py-3.5 bg-green-800 hover:bg-green-900 text-white text-base font-semibold shadow-md hover:shadow-lg transition-shadow"
                      data-testid="pricing-subscribe-button"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        ctaLabel
                      )}
                    </Button>
                    {trialActive && !portalBroken && (
                      <p
                        className="mt-3 text-xs text-green-800 text-center font-medium"
                        data-testid="pricing-trial-active-note"
                      >
                        {t("pricing.trialActiveNote")}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-gray-500 text-center" data-testid="pricing-cta-subtext">
                      {t("pricing.subText")}
                    </p>
                    <p className="mt-4 text-xs text-gray-500 flex items-center justify-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" /> {t("pricing.secureLine")}
                    </p>
                    {/* Trust/value block — calm professional reassurance, not
                        a marketing banner. Sits between the secure-payment
                        line and the refund/cancel fine print so it reads as
                        the natural close of the offer before the legal copy. */}
                    <div
                      className="mt-6 text-center"
                      data-testid="pricing-trust-block"
                    >
                      <p
                        className="text-sm font-semibold text-gray-800"
                        data-testid="pricing-trust-heading"
                      >
                        {t("pricing.trustHeading")}
                      </p>
                      <p
                        className="mt-1.5 text-xs text-gray-500 leading-relaxed"
                        data-testid="pricing-trust-line-1"
                      >
                        {t("pricing.trustLine1")}
                      </p>
                      <p
                        className="mt-1 text-xs text-green-800/90 leading-relaxed"
                        data-testid="pricing-trust-line-2"
                      >
                        {t("pricing.trustLine2")}
                      </p>
                    </div>
                    <div
                      className="mt-6 pt-5 border-t border-gray-200 space-y-1.5 text-xs text-gray-500 text-center"
                      data-testid="pricing-billing-terms"
                    >
                      <p data-testid="pricing-no-refunds">
                        {t("pricing.noRefunds")}
                      </p>
                      <p data-testid="pricing-cancel-policy">
                        {t("pricing.cancelPolicy")}
                      </p>
                      <p>
                        {t("pricing.needHelp")}{" "}
                        <a
                          href="mailto:support@tasklio.co"
                          className="text-green-800 hover:underline font-medium"
                          data-testid="pricing-support-link"
                        >
                          {t("pricing.contactLink")}
                        </a>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-16 grid sm:grid-cols-3 gap-6 text-center max-w-3xl mx-auto">
            {[
              { h: t("pricing.benefitCancelTitle"), b: t("pricing.benefitCancelBody") },
              { h: t("pricing.benefitTrialTitle"), b: t("pricing.benefitTrialBody") },
              { h: t("pricing.benefitBuiltTitle"), b: t("pricing.benefitBuiltBody") },
            ].map((i) => (
              <div key={i.h} className="p-4">
                <p className="font-display font-medium text-gray-900">{i.h}</p>
                <p className="mt-1 text-sm text-gray-500">{i.b}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
